/**
 * Creates payload items and endpoint configuration for Esntial eligibility checks.
 * This utility extracts common logic used by both VDP and SRP Esntial eligibility hooks.
 *
 * @param {Object} listing - The vehicle listing object
 * @param {boolean} enableV2 - Whether V2 API is enabled via feature flag
 * @returns {Object} Configuration object containing payloadItems, endpoints, and version info
 * @returns {Object} returns.payloadItems - Formatted payload for V2 API calls
 * @returns {string} returns.v1EsntialEndpoint - V1 endpoint URL
 * @returns {string} returns.eligibilityURL - Selected endpoint URL based on version
 * @returns {boolean} returns.isV2Enabled - Whether V2 is enabled and endpoint is V2
 */
export function getEsntialEligibilityConfig(listing, enableV2) {
    if (!listing || !Object.keys(listing).length) {
        return {
            payloadItems: null,
            v1EsntialEndpoint: null,
            eligibilityURL: null,
            isV2Enabled: false,
        };
    }

    const {
        esntial,
        year,
        make,
        model,
        trim: listingTrim,
        mileage: listingMileage,
        listingType,
        pricingDetail = {},
        owner: listingOwner,
        marketExtension: { parentOwnerId: parentId } = {},
        vin,
    } = listing;

    // Extract pricing information
    const { incentive, msrp, salePrice } = pricingDetail;
    const isCertified = listingType?.toLowerCase() === 'certified';
    const listingPrice = incentive || salePrice || msrp;

    // Normalize data types (handle both object and string formats)
    const mileage = typeof listingMileage === 'object' ? listingMileage?.value : listingMileage;
    const ownerId = parentId || (typeof listingOwner === 'object' ? listingOwner?.id : listingOwner);
    const trim = typeof listingTrim === 'object' ? listingTrim?.name : listingTrim;
    const vehicleCondition = listingType?.toLowerCase() === 'certified' ? 'used' : listingType?.toLowerCase();

    // Create payload items for V2 API
    const payloadItems = {
        isCertified,
        listingPrice,
        make: typeof make === 'object' ? make?.name : make,
        mileage,
        model: typeof model === 'object' ? model?.name : model,
        msrp,
        ownerId,
        trim,
        vehicleCondition,
        vin,
        year,
    };

    // Generate endpoint URLs
    const v1EsntialEndpoint = esntial?.eligibilityService?.replace('vehicle/v2/verify', 'search/verify');
    const eligibilityURL = enableV2 ? esntial?.eligibilityService : v1EsntialEndpoint;
    const isV2Enabled = enableV2 && eligibilityURL?.includes('/v2/');

    return {
        payloadItems,
        eligibilityURL,
        isV2Enabled,
    };
}

export default getEsntialEligibilityConfig;
